import { memo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ModalBody, ModalContent, Modal, ModalHeader, ModalFooter } from '@heroui/react';
import { CalendarDate, getLocalTimeZone, today as getToday } from '@internationalized/date';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  useCreateIntakeMutation,
  StudyFormat,
  StudyMode,
  STUDY_FORMAT_LABELS,
  STUDY_MODE_LABELS,
} from '@/entities/Courses';
import { AppButton } from '@/shared/ui/AppButton';
import { CustomInput, CustomDateRangePicker, CustomSelect } from '@/shared/ui/Fields';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import { useToast } from '@/shared/lib/hooks/useToast';
import styles from './AddIntakeModal.module.scss';

interface AddIntakeModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  courseId: number;
  onSuccess?: () => void;
}

interface IntakeFormData {
  title: string;
  dateRange: { start: CalendarDate; end: CalendarDate } | null;
  studyFormat: string;
  studyMode: string;
}

const STUDY_FORMAT_ITEMS = Object.values(StudyFormat).map((format) => ({
  key: format,
  label: STUDY_FORMAT_LABELS[format],
}));

const STUDY_MODE_ITEMS = Object.values(StudyMode).map((mode) => ({
  key: mode,
  label: STUDY_MODE_LABELS[mode],
}));

const intakeSchema = yup.object({
  title: yup.string().required('Intake name is required').min(1).max(255),
  dateRange: yup
    .mixed<{ start: CalendarDate; end: CalendarDate }>()
    .nullable()
    .required('Intake duration is required')
    .test('min-date', (value, context) => {
      if (!value || !value.start) return true;
      const today = getToday(getLocalTimeZone());
      const startDate = value.start;
      if (
        startDate.year < today.year ||
        (startDate.year === today.year && startDate.month < today.month) ||
        (startDate.year === today.year &&
          startDate.month === today.month &&
          startDate.day < today.day)
      ) {
        const dateStr = `${String(today.day).padStart(2, '0')}.${String(today.month).padStart(2, '0')}.${today.year}`;
        return context.createError({
          message: `Value must be no earlier than ${dateStr}.`,
        });
      }
      return true;
    }),
  studyFormat: yup
    .string()
    .required('Format is required')
    .oneOf(Object.values(StudyFormat), 'Select a valid format'),
  studyMode: yup
    .string()
    .required('Commitment is required')
    .oneOf(Object.values(StudyMode), 'Select a valid commitment'),
}) as yup.ObjectSchema<IntakeFormData>;

export const AddIntakeModal = memo(
  ({ isOpen, onOpenChange, courseId, onSuccess }: AddIntakeModalProps) => {
    const { toast } = useToast();
    const [createIntake, { isLoading }] = useCreateIntakeMutation();

    const {
      control,
      handleSubmit,
      watch,
      formState: { errors },
      reset,
      trigger,
    } = useForm<IntakeFormData>({
      resolver: yupResolver(intakeSchema) as any,
      defaultValues: {
        title: '',
        dateRange: null,
        studyFormat: '',
        studyMode: '',
      },
    });

    const title = watch('title');
    const dateRange = watch('dateRange');
    const studyFormat = watch('studyFormat');
    const studyMode = watch('studyMode');
    const isFormFilled = Boolean(
      title?.trim() && dateRange?.start && dateRange?.end && studyFormat && studyMode,
    );

    const onSubmit = async (data: IntakeFormData) => {
      if (!data.dateRange || !data.dateRange.start || !data.dateRange.end) {
        toast({
          title: 'Error',
          description: 'Intake duration is required',
          color: 'danger',
        });
        return;
      }

      try {
        const formatDate = (date: CalendarDate): string =>
          `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;

        const startDate = formatDate(data.dateRange.start);
        const endDate = formatDate(data.dateRange.end);

        const result = await createIntake({
          courseId,
          title: data.title,
          startDate,
          endDate,
          studyFormat: data.studyFormat,
          studyMode: data.studyMode,
        }).unwrap();

        toast({
          title: 'Success',
          description: result.message || 'Intake created successfully',
          color: 'success',
        });

        handleClose();
        onSuccess?.();
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error?.data?.message || 'Failed to create intake',
          color: 'danger',
        });
      }
    };

    const handleClose = () => {
      reset({
        title: '',
        dateRange: null,
        studyFormat: '',
        studyMode: '',
      });
      onOpenChange(false);
    };

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={handleClose}
        placement="center"
        motionProps={scaleInOut}
        size="2xl"
        classNames={{
          base: 'p-[36px] flex flex-col gap-5 max-w-[552px]',
          closeButton: styles.closeBtn,
        }}
      >
        <ModalContent>
          {() => (
            <form onSubmit={handleSubmit(onSubmit as any)}>
              <ModalHeader className={styles.title}>Add new intake</ModalHeader>
              <ModalBody className="p-0 flex flex-col gap-6">
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      {...field}
                      label="Intake name"
                      labelPlacement="outside"
                      placeholder="Social Media Strategy in 6 Steps"
                      errorMessage={errors.title?.message}
                      showError={!!errors.title}
                      isRequired
                    />
                  )}
                />

                <Controller
                  name="studyFormat"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      style="black"
                      label="Format"
                      labelPlacement="outside"
                      placeholder="Select format"
                      items={STUDY_FORMAT_ITEMS}
                      selectedKeys={field.value ? new Set([field.value]) : new Set()}
                      onSelectionChange={(keys) => {
                        const selected = Array.from(keys)[0] as string;
                        field.onChange(selected);
                      }}
                      errorMessage={errors.studyFormat?.message}
                      showError={!!errors.studyFormat}
                      isRequired
                    />
                  )}
                />

                <Controller
                  name="studyMode"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      style="black"
                      label="Commitment"
                      labelPlacement="outside"
                      placeholder="Select commitment"
                      items={STUDY_MODE_ITEMS}
                      selectedKeys={field.value ? new Set([field.value]) : new Set()}
                      onSelectionChange={(keys) => {
                        const selected = Array.from(keys)[0] as string;
                        field.onChange(selected);
                      }}
                      errorMessage={errors.studyMode?.message}
                      showError={!!errors.studyMode}
                      isRequired
                    />
                  )}
                />

                <Controller
                  name="dateRange"
                  control={control}
                  render={({ field }) => (
                    <CustomDateRangePicker
                      label="Intake Duration"
                      labelPlacement="outside"
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        trigger('dateRange');
                      }}
                      errorMessage={errors.dateRange?.message || undefined}
                      showError={!!errors.dateRange}
                    />
                  )}
                />
              </ModalBody>
              <ModalFooter className="gap-4 pt-6 pb-0 px-0">
                <AppButton fullWidth color="white" onPress={handleClose} type="button">
                  Cancel
                </AppButton>
                <AppButton
                  fullWidth
                  color="black"
                  type="submit"
                  isLoading={isLoading}
                  disabled={!isFormFilled}
                  className={!isFormFilled ? styles.createButtonDisabled : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmit as any)();
                  }}
                >
                  Create intake
                </AppButton>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>
    );
  },
);

AddIntakeModal.displayName = 'AddIntakeModal';
